import React, { ReactElement, useMemo, useState } from "react";
import BarsChart, { Item } from "../barsChart/BarsChart";
import st from './BarsChartWithLegend.module.scss';
import Legend, { LegendRow } from "../Legend/Legend";
import { Tooltip } from "@infobis/ui-styles-utils";
import { CircleLabel } from "@pages/dashboard/works/detalization/dynamics";
import { formatNumber2d } from "@utils/format/number";
import CropsRowRenderer from "@elements/Legend/components/CropsRowRenderer/CropsRowRenderer";

type BarsChartProps<Custom extends Record<string, any>> = Pick<
	React.ComponentProps<typeof BarsChart<Custom>>,
	'tooltipProps' | 'getTooltipText' | 'suppressTooltipStyles' | 'gap' | 'items' | 'compareFn'
>

type BarsChartWithLegendProps<Custom extends Record<string, any> = {}> = {
	suppressLegendClick?: boolean
	componentGap?: number
	disabledByDefaultElements?: string[]
	onLegendClick?: (row: LegendRow) => void
	legendProps?: Partial<React.ComponentProps<typeof Legend>>
	getLegendRowValues?: (row: Item<Custom>) => ReactElement[]
	height?: number
} & BarsChartProps<Custom>

const getDefaultLegendRowValues = (item: Item) => {
	return [
		<Tooltip.Flex tooltipProps={{ bodyClassName: st['tooltip__body'] }}
					  text={<CircleLabel align={'start'} color={item.color} name={item.name} ellipsis/>} />,
		<Tooltip.Flex tooltipProps={{ bodyClassName: st['tooltip__body'] }}
					  text={
						  <span style={{ textAlign: 'end' }} className={st["chart__legend__value"]}>
							  {formatNumber2d(item.value)}
						  </span>
					  }
		/>,
	]
}

const BarsChartWithLegend = <Custom extends Record<string, any>,>(props: BarsChartWithLegendProps<Custom>) => {
	const {
		componentGap = 16,
		height = 24,
		suppressLegendClick,
		disabledByDefaultElements = [],
		items,
		onLegendClick = () => {},
		legendProps,
		getLegendRowValues = getDefaultLegendRowValues,
		...restProps
	} = props;

	const toggleItemVisibility = (item: Item<Custom>): Item<Custom> => {
		if (item.value === 0) return { ...item, value: items.find(i => i.id === item.id)?.value || 0 }

		return { ...item, value: 0 }
	}

	const [visibleElements, setVisibleElements] = useState(
		items.reduce((acc, cur) => {
			if (disabledByDefaultElements.includes(cur.id)) {
				return [...acc, toggleItemVisibility(cur)]
			}

			return [...acc, cur]
		}, [] as Item<Custom>[])
	)

	const handleLegendClick = (row: LegendRow) => {
		if (!suppressLegendClick) {
			setVisibleElements(
				visibleElements.reduce((acc, cur) => {
					if (cur.id === row.id) return [...acc, toggleItemVisibility(cur)]

					return [...acc, cur]
				}, [] as Item<Custom>[])
			)
		}

		onLegendClick(row)
	}

	const legendRows: LegendRow[] = useMemo(() => {
		const sortedRows = restProps.compareFn ? [...items].sort(restProps.compareFn) : items

		return sortedRows.map(item => ({
			id: item.id,
			color: item.color,
			values: getLegendRowValues(item)
		}))
	}, [items])

	return (
		<div className={st['wrapper']} style={{ gap: componentGap }}>
			<BarsChart height={height}
					   items={visibleElements.filter(element => element.value)}
					   {...restProps} />
			<Legend rows={legendRows}
					disabledByDefaultOptions={disabledByDefaultElements}
					rowRenderer={legendProps?.rowRenderer || (row => <CropsRowRenderer row={row}/>)}
					gap={legendProps?.gap || 6}
					onClick={handleLegendClick}
					{...legendProps} />
		</div>
	)
}

export default BarsChartWithLegend;